import axios, { AxiosResponse } from 'axios';
import memoize from 'lodash.memoize';

import { retrieveSessionFromStorage } from '_helpers/queryInterceptorHelpers';
import { constants } from 'appConstants';

import { AccessToken, RefreshToken, Session } from '@/types/auth';

/**
 * Refresh the JWT access token using the stored refresh token.
 *
 * @returns A promise resolving to the updated Session object, or null if the refresh fails.
 */
const refreshSession = async(): Promise<Session | null> => {
  const previousSession: Session | null = retrieveSessionFromStorage()
  if (!previousSession) {
    sessionStorage.removeItem("session");
    return null;
  }

  const requestOptions = {
    method: "POST",
    url: `${constants.apiUrl}/token/refresh/`,
    data: { refresh: previousSession.refreshToken } as RefreshToken,
    headers: {
      Accept: "application/json",
    },
    timeout: 10000,
    withCredentials: false,
  };

  try {
    const response: AxiosResponse<AccessToken> = await axios(requestOptions);
    const accessToken = response?.data?.access;
    if (typeof accessToken !== "string") {
      sessionStorage.removeItem("session");
      return null;
    }

    const refreshedSession: Session = {
      id: previousSession.id,
      refreshToken: previousSession.refreshToken,
      accessToken: accessToken,
    };
    sessionStorage.setItem("session", JSON.stringify(refreshedSession));
    return refreshedSession;
  } catch (error) {
    console.warn("Failed to refresh session:", error);
    sessionStorage.removeItem('session');
    return null;
  }
}

export const memoizedRefreshedSession = memoize(refreshSession);